import { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useAuth } from '../contexts/AuthContext';
import { Leaf } from 'lucide-react';

export function Login({ initialEmail }: { initialEmail?: string } = {}) {
  const { login, bootstrapSustaintrack } = useAuth();
  const [email, setEmail] = useState(initialEmail ?? '');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email.trim(), password);
    } catch (err: any) {
      setError(err?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const onBootstrap = async () => {
    const adminEmail = prompt('Admin email for initial Sustaintrack org?', 'admin@sustaintrack.io') || '';
    const adminPassword = prompt('Admin password?') || '';
    if (!adminEmail || !adminPassword) return;
    setError(null);
    setLoading(true);
    try {
      await bootstrapSustaintrack(adminEmail, adminPassword, 'Admin');
    } catch (err: any) {
      setError(err?.message || 'Bootstrap failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <div className="flex items-center justify-center mb-6">
        <div className="flex items-center space-x-2">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-blue-500 shadow-sm">
            <Leaf className="h-5 w-5 text-white" />
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-green-700 text-lg leading-tight">SustainTrack</span>
            <span className="text-xs text-green-600 leading-tight">Sign in to continue</span>
          </div>
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-4 bg-white/70 backdrop-blur-sm p-6 rounded-lg border shadow">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
        </div>
        {error && <div className="text-sm text-red-600">{error}</div>}
        <Button type="submit" className="w-full bg-green-600 hover:bg-green-700" disabled={loading}>
          {loading ? 'Signing in…' : 'Sign in'}
        </Button>
        <Button type="button" variant="outline" className="w-full" onClick={onBootstrap} disabled={loading}>
          Bootstrap Sustaintrack (admin)
        </Button>
        <p className="text-sm text-center text-muted-foreground pt-2">
          Pas encore de compte ?{' '}
          <a href="?register" className="text-green-700 underline hover:text-green-800">
            Créer un compte
          </a>
        </p>
      </form>
    </div>
  );
}
